import React, { useEffect, useState, useRef, useMemo } from 'react';
import {
  FormControl,
  FormHelperText,
  InputBaseComponentProps,
  InputLabel,
  OutlinedInput,
  SxProps,
  useTheme,
  Box,
  Typography,
  Popper,
  Fade,
  Paper,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { INTEGER_REGEXP, NUMBER_REGEXP } from '../../constants/regEx';

export interface TextFieldProps {
  placeholder?: string;
  label?: string;
  endAdornment?: React.ReactNode;
  startAdornment?: React.ReactNode;
  className?: string;
  inputClassName?: string;
  type?: 'number' | 'integer' | 'string' | 'password' | 'text';
  style?: React.CSSProperties;
  disabled?: boolean;
  value?: string | number;
  onChange?: React.ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement>;
  onBlur?: React.ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement>;
  onPaste?: React.ClipboardEventHandler<HTMLDivElement>;
  onClick?: React.MouseEventHandler<HTMLDivElement> | undefined;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  onFocus?: React.FocusEventHandler<HTMLTextAreaElement | HTMLInputElement>;
  error?: string;
  name?: string;
  multiline?: boolean;
  required?: boolean;
  number?: boolean;
  /* eslint-disable @typescript-eslint/no-explicit-any */
  inputRef?: React.Ref<any>;
  sx?: SxProps;
  rows?: number;
  showInputProps?: boolean;
  maxLength?: number;
  helperText?: string;
  showCharCount?: boolean;
  validationState?: {
    loading?: boolean;
    valid?: boolean | null;
    suggestion?: string | null;
    suggestions?: string[];
    error?: string | null;
  };
  onApplySuggestion?: (suggestion: string) => void;
}

function ExplanationPopover({ suggestion, label }: { suggestion: string; label?: string }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <Box>
      <span
        onClick={() => setExpanded(!expanded)}
        style={{
          fontSize: '0.75rem',
          fontWeight: 600,
          color: '#7c4dff',
          cursor: 'pointer',
          textDecoration: 'underline',
          userSelect: 'none',
        }}
      >
        {expanded ? 'Hide explanation' : 'Why this suggestion?'}
      </span>
      {expanded && (
        <Box
          sx={{
            mt: '8px',
            p: '10px',
            borderRadius: '8px',
            backgroundColor: 'rgba(0, 0, 0, 0.02)',
            border: '1px dashed rgba(124, 77, 255, 0.25)',
            fontSize: '0.75rem',
            color: 'text.secondary',
            animation: 'fadeInScale 0.2s ease',
          }}
        >
          <strong>✨ AI Reasoning:</strong>
          <br />
          The AI detected that the formatting, postal rules, or domain records for the entered {label || 'field'} might
          be invalid or have typos, and recommends using the suggested value "{suggestion}" for optimal validation and
          courier routing.
        </Box>
      )}
    </Box>
  );
}

function SbTextField(props: TextFieldProps) {
  const { type, showInputProps = true, maxLength, value = '', showCharCount } = props;
  const [charCount, setCharCount] = useState<number | undefined>(value ? String(value).length : undefined);
  const theme = useTheme();

  const [timelineMessage, setTimelineMessage] = useState('');
  const [lastValidatedTime, setLastValidatedTime] = useState<string>('');
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [isBadgeHovered, setIsBadgeHovered] = useState(false);
  const [isPopoverHovered, setIsPopoverHovered] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const prevLoading = useRef<boolean | undefined>(props.validationState?.loading);

  const loading = props.validationState?.loading;
  const valid = props.validationState?.valid;

  useEffect(() => {
    if (value) {
      setCharCount(String(value).length);
    } else {
      setCharCount(undefined);
    }
  }, [value]);

  useEffect(() => {
    if (props.validationState?.valid || props.validationState?.suggestion || props.validationState?.error) {
      setLastValidatedTime(
        new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
      );
    }
  }, [props.validationState?.valid, props.validationState?.suggestion, props.validationState?.error]);

  useEffect(() => {
    if (loading === true) {
      setTimelineMessage('✨ AI is analyzing...');

      // Dynamic timeline status updates
      const t1 = setTimeout(() => {
        setTimelineMessage('✨ Checking validation rules...');
      }, 1200);

      return () => {
        clearTimeout(t1);
      };
    } else if (loading === false && valid === false) {
      if (props.validationState?.suggestion) {
        setTimelineMessage('✨ AI Suggestion Available');
      } else {
        setTimelineMessage('✨ AI Found an Issue');
      }
    } else {
      setTimelineMessage('');
    }

    prevLoading.current = loading;
  }, [loading, valid, props.validationState?.suggestion]);

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    if (props.onChange) {
      if (type === 'number' || type === 'integer') {
        const regExp = type === 'number' ? NUMBER_REGEXP : INTEGER_REGEXP;
        if (regExp.test(event.target.value) || event.target.value === '') {
          props.onChange(event);
          setCharCount(value.length);
        }
      } else {
        props.onChange(event);
        setCharCount(value.length);
      }
    }
  };

  const handleWheel = (e: React.WheelEvent<HTMLInputElement>) => {
    if (type === 'number' || type === 'integer') {
      (e.target as HTMLInputElement).blur();
    }
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setIsInputFocused(true);
    if (props.onFocus) {
      props.onFocus(e);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setIsInputFocused(false);
    if (props.onBlur) {
      props.onBlur(e);
    }
  };

  const inputProps: InputBaseComponentProps = {
    ...(showInputProps && type === 'number' ? { type: 'number', step: '0.01', min: '1' } : {}),
    ...(maxLength ? { maxLength: maxLength } : {}),
  };

  const hasFrontendError = !!props.error;
  const hasAiError = !!props.validationState?.error;
  const hasError = hasFrontendError || hasAiError;

  // The popover should only open when the field is focused or badge/popover is hovered,
  // AND either we are actively loading OR validation failed (valid === false) with suggestion/error.
  const isPopoverOpen =
    (isInputFocused || isBadgeHovered || isPopoverHovered) &&
    (loading === true || (valid === false && (!!props.validationState?.suggestion || hasAiError)));

  const borderStyle = (() => {
    if (loading) {
      return {
        border: '1px solid transparent',
        backgroundImage:
          theme.palette.mode === 'dark'
            ? 'linear-gradient(#121212, #121212), linear-gradient(270deg, #7c4dff, #ff4081, #00e5ff, #7c4dff)'
            : 'linear-gradient(#fff, #fff), linear-gradient(270deg, #7c4dff, #ff4081, #00e5ff, #7c4dff)',
        backgroundOrigin: 'border-box',
        backgroundClip: 'padding-box, border-box',
        backgroundSize: '400% 400%',
        animation: 'geminiGlow 3s ease infinite',
        boxShadow: '0 0 10px 1px rgba(124, 77, 255, 0.25)',
      };
    }
    // Match visual reference for soft orange/yellow border when suggestion is available
    if (valid === false && props.validationState?.suggestion) {
      return {
        border: '1px solid #ff9800',
        boxShadow: '0 0 8px 1px rgba(255, 152, 0, 0.2)',
      };
    }
    return {
      border: hasError ? `1px solid ${theme.palette.error.main}` : `1px solid ${theme.palette.divider}`,
    };
  })();

  const paperStyle = useMemo(() => {
    if (loading === true) {
      return {
        boxShadow:
          theme.palette.mode === 'dark' ? '0 8px 32px rgba(0, 0, 0, 0.5)' : '0 8px 32px rgba(124, 77, 255, 0.12)',
        border:
          theme.palette.mode === 'dark' ? '1px solid rgba(124, 77, 255, 0.25)' : '1px solid rgba(124, 77, 255, 0.15)',
        background:
          theme.palette.mode === 'dark'
            ? 'linear-gradient(135deg, #1e1e24 0%, #121214 100%)'
            : 'linear-gradient(135deg, #ffffff 0%, #fcfbfe 100%)',
        borderLeft: '4px solid #7c4dff',
      };
    }
    // Light warning style matching image reference
    if (valid === false && props.validationState?.suggestion) {
      return {
        boxShadow:
          theme.palette.mode === 'dark' ? '0 8px 32px rgba(0, 0, 0, 0.5)' : '0 8px 32px rgba(255, 152, 0, 0.1)',
        border: theme.palette.mode === 'dark' ? '1px solid #78350f' : '1px solid #ffcc80',
        background: theme.palette.mode === 'dark' ? '#24170a' : '#fffaf2',
        borderLeft: '4px solid #ff9800',
      };
    }
    // Error state
    return {
      boxShadow: theme.palette.mode === 'dark' ? '0 8px 32px rgba(0, 0, 0, 0.5)' : '0 8px 32px rgba(211, 47, 47, 0.1)',
      border: theme.palette.mode === 'dark' ? '1px solid #7f1d1d' : '1px solid #fecaca',
      background: theme.palette.mode === 'dark' ? '#241010' : '#fff5f5',
      borderLeft: `4px solid ${theme.palette.error.main}`,
    };
  }, [loading, valid, props.validationState?.suggestion, theme.palette.mode, theme.palette.error.main]);

  const tooltipInnerContent = useMemo(() => {
    // Loading State
    if (loading === true) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#7c4dff' }}>
            <AutoAwesomeIcon sx={{ fontSize: '1rem', animation: 'sparkleRotate 2s linear infinite' }} />
            <Typography
              sx={{ fontSize: '0.8rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}
            >
              AI Analyzing
            </Typography>
          </Box>
          <Typography sx={{ fontSize: '0.85rem', color: 'text.primary', animation: 'shimmerText 1.5s ease infinite' }}>
            {timelineMessage || '✨ AI is analyzing...'}
          </Typography>
        </Box>
      );
    }

    // Case 2: AI Suggestion (Matches image reference layout)
    if (valid === false && props.validationState?.suggestion) {
      const suggestedValue = props.validationState.suggestion;
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <AutoAwesomeIcon sx={{ fontSize: '1rem', color: '#f59e0b' }} />
              <Typography
                sx={{
                  fontSize: '0.8rem',
                  fontWeight: 700,
                  color: '#f59e0b',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}
              >
                AI Suggestion
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                backgroundColor: 'rgba(245, 158, 11, 0.1)',
                px: '6px',
                py: '2px',
                borderRadius: '4px',
              }}
            >
              <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: '#f59e0b' }}>98% Match</Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
            <Typography sx={{ fontSize: '0.875rem', color: 'text.primary', fontWeight: 500, flex: 1, lineHeight: 1.4 }}>
              {props.validationState.error ||
                `This ${props.label || 'value'} doesn't match — did you mean ${suggestedValue}?`}
            </Typography>

            {props.onApplySuggestion && (
              <button
                type="button"
                aria-label={`Use ${suggestedValue}`}
                onClick={() => {
                  if (props.onApplySuggestion) {
                    props.onApplySuggestion(suggestedValue);
                  }
                }}
                style={{
                  backgroundColor: '#b45309', // Pill button color matching image
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '20px', // Round pill matching image
                  padding: '6px 16px',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  whiteSpace: 'nowrap',
                  boxShadow: '0 2px 4px rgba(180, 83, 9, 0.2)',
                }}
                onMouseOver={e => {
                  e.currentTarget.style.filter = 'brightness(1.1)';
                }}
                onMouseOut={e => {
                  e.currentTarget.style.filter = 'none';
                }}
              >
                Use {suggestedValue}
              </button>
            )}
          </Box>

          <Box sx={{ borderTop: '1px solid rgba(245, 158, 11, 0.15)', pt: 1 }}>
            <ExplanationPopover suggestion={suggestedValue} label={props.label} />
          </Box>
        </Box>
      );
    }

    // Case 3: AI Validation Failure
    if (valid === false && !props.validationState?.suggestion) {
      const suggestionsList =
        props.validationState?.suggestions && props.validationState.suggestions.length > 0
          ? props.validationState.suggestions
          : props.validationState?.error
          ? props.validationState.error.split('\n').filter(Boolean)
          : [];

      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <AutoAwesomeIcon sx={{ fontSize: '1rem', color: theme.palette.error.main }} />
            <Typography
              sx={{
                fontSize: '0.8rem',
                fontWeight: 700,
                color: theme.palette.error.main,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              AI Validation
            </Typography>
          </Box>

          {suggestionsList.length > 1 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, my: 0.5 }}>
              {suggestionsList.map((item, idx) => (
                <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <Typography sx={{ fontSize: '0.8rem', color: theme.palette.error.main, lineHeight: 1.4 }}>
                    •
                  </Typography>
                  <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', lineHeight: 1.4 }}>{item}</Typography>
                </Box>
              ))}
            </Box>
          ) : suggestionsList.length === 1 ? (
            <Typography sx={{ fontSize: '0.85rem', color: 'text.secondary', lineHeight: 1.4 }}>
              {suggestionsList[0]}
            </Typography>
          ) : (
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'text.primary' }}>
              This value couldn't be verified by AI.
            </Typography>
          )}

          <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
            Please verify before continuing.
          </Typography>
        </Box>
      );
    }

    return null;
  }, [
    loading,
    valid,
    props.validationState?.suggestion,
    props.validationState?.suggestions,
    props.validationState?.error,
    lastValidatedTime,
    props.onApplySuggestion,
    props.label,
    theme.palette.mode,
    theme.palette.error.main,
    timelineMessage,
  ]);

  return (
    <div ref={containerRef} style={props.style || {}} className={`sb-textfield-wrapper ${props.className || ''}`}>
      <style>{`
        @keyframes geminiGlow {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        @keyframes sparkleRotate {
          0% { transform: rotate(0deg) scale(1); opacity: 0.7; }
          50% { transform: rotate(180deg) scale(1.25); opacity: 1; }
          100% { transform: rotate(360deg) scale(1); opacity: 0.7; }
        }
        @keyframes fadeInScale {
          from { opacity: 0; transform: scale(0.96); }
          to { opacity: 1; transform: scale(1); }
        }
        @keyframes shimmerText {
          0%, 100% { opacity: 0.55; }
          50% { opacity: 0.95; }
        }
        @media (prefers-reduced-motion: reduce) {
          .sb-textfield-input, .MuiOutlinedInput-root, * {
            animation: none !important;
            transition: none !important;
          }
        }
      `}</style>

      {props.label && (
        <InputLabel
          className="sb-textfield-label"
          required={props.required}
          sx={{
            fontWeight: 500,
            fontSize: '0.875rem',
            marginBottom: '8px',
            display: 'block',
            letterSpacing: '0.3px',
            transition: 'color 0.2s ease',
            '&.Mui-focused': {
              color: hasError ? theme.palette.error.main : theme.palette.primary.main,
            },
          }}
        >
          {props.label}
        </InputLabel>
      )}
      <FormControl className={`sb-textfield-control ${props.inputClassName || ''}`} fullWidth error={hasError}>
        <OutlinedInput
          multiline={props.multiline}
          rows={props.rows}
          error={hasError}
          name={props.name}
          type={props.type === 'number' ? 'string' : props.type}
          className="sb-textfield-input"
          placeholder={props.placeholder || ''}
          endAdornment={
            <React.Fragment>
              {loading && (
                <Box
                  onMouseEnter={() => setIsBadgeHovered(true)}
                  onMouseLeave={() => setIsBadgeHovered(false)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    color: '#7c4dff',
                    mr: 1,
                    cursor: 'help',
                    flexShrink: 0,
                  }}
                >
                  <AutoAwesomeIcon sx={{ fontSize: '1.05rem', animation: 'sparkleRotate 2s linear infinite' }} />
                  <span style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>
                    AI
                  </span>
                </Box>
              )}
              {/* Only show AI Indicator if validation has run and failed (valid === false) */}
              {!loading && valid === false && (props.validationState?.suggestion || hasAiError) && (
                <Box
                  onMouseEnter={() => setIsBadgeHovered(true)}
                  onMouseLeave={() => setIsBadgeHovered(false)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    color: props.validationState?.suggestion ? '#ff9800' : '#d32f2f',
                    mr: 1,
                    cursor: 'help',
                    animation: 'fadeInScale 0.3s ease',
                    flexShrink: 0,
                  }}
                >
                  <AutoAwesomeIcon sx={{ fontSize: '0.9rem', opacity: 0.9 }} />
                  <span style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>
                    {props.validationState?.suggestion ? 'AI' : 'AI Alert'}
                  </span>
                </Box>
              )}
              {props.endAdornment || <></>}
            </React.Fragment>
          }
          startAdornment={props.startAdornment || <></>}
          sx={{
            ...props.sx,
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(18, 18, 18, 0.7)' : 'rgba(255, 255, 255, 0.7)',
            backdropFilter: 'blur(10px)',
            borderRadius: '12px',
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',

            '& .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },

            border: borderStyle.border,
            backgroundImage: borderStyle.backgroundImage || 'none',
            backgroundOrigin: borderStyle.backgroundOrigin || 'initial',
            backgroundClip: borderStyle.backgroundClip || 'initial',
            backgroundSize: borderStyle.backgroundSize || 'initial',
            animation: borderStyle.animation || 'none',
            boxShadow: borderStyle.boxShadow || 'none',

            '& .MuiOutlinedInput-input': {
              fontSize: '0.95rem',
              fontWeight: 400,
              color: theme.palette.text.primary,
              padding: '12px 16px',
              letterSpacing: '0.3px',
              transition: 'all 0.2s ease',

              '&::placeholder': {
                color: theme.palette.text.secondary,
                opacity: 0.6,
              },

              '&:autofill, &:-webkit-autofill': {
                WebkitBoxShadow: `0 0 0 1000px ${theme.palette.background.paper} inset !important`,
                WebkitTextFillColor: `${theme.palette.text.primary} !important`,
              },
            },

            '&.Mui-focused': {
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(18, 18, 18, 0.85)' : 'rgba(255, 255, 255, 0.85)',
              backdropFilter: 'blur(12px)',
              border: loading
                ? borderStyle.border
                : valid === false && props.validationState?.suggestion
                ? '1px solid #ff9800'
                : hasError
                ? `1px solid ${theme.palette.error.main}`
                : `1px solid ${theme.palette.primary.main}`,
              backgroundImage: loading ? borderStyle.backgroundImage : 'none',
              backgroundOrigin: loading ? borderStyle.backgroundOrigin : 'initial',
              backgroundClip: loading ? borderStyle.backgroundClip : 'initial',
              backgroundSize: loading ? borderStyle.backgroundSize : 'initial',
              animation: loading ? borderStyle.animation : 'none',
              boxShadow: loading
                ? borderStyle.boxShadow
                : valid === false && props.validationState?.suggestion
                ? '0 0 0 2px rgba(255, 152, 0, 0.2)'
                : hasError
                ? `0 0 0 2px ${theme.palette.error.main}20`
                : `0 0 0 2px ${theme.palette.primary.main}20`,
            },

            '&:hover:not(.Mui-focused):not(.Mui-disabled)': {
              backgroundColor: theme.palette.mode === 'dark' ? 'rgba(18, 18, 18, 0.8)' : 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(12px)',
              borderColor: loading
                ? 'transparent'
                : valid === false && props.validationState?.suggestion
                ? '#ff9800'
                : hasError
                ? theme.palette.error.main
                : theme.palette.primary.main,
              border:
                loading || (valid === false && !!props.validationState?.suggestion) ? borderStyle.border : undefined,
              backgroundImage: loading ? borderStyle.backgroundImage : undefined,
              backgroundOrigin: loading ? borderStyle.backgroundOrigin : undefined,
              backgroundClip: loading ? borderStyle.backgroundClip : undefined,
              backgroundSize: loading ? borderStyle.backgroundSize : undefined,
              animation: loading ? borderStyle.animation : undefined,
              boxShadow:
                loading || (valid === false && !!props.validationState?.suggestion) ? borderStyle.boxShadow : undefined,
            },

            '&.Mui-disabled': {
              backgroundColor: theme.palette.action.disabledBackground,
              backdropFilter: 'blur(8px)',
              borderColor: theme.palette.divider,
              cursor: 'not-allowed',

              '& .MuiOutlinedInput-input': {
                color: theme.palette.text.disabled,
                WebkitTextFillColor: theme.palette.text.disabled,
                cursor: 'not-allowed',
              },
            },

            '&.MuiOutlinedInput-multiline': {
              padding: 0,

              '& .MuiOutlinedInput-input': {
                padding: '12px 16px',
              },
            },

            '& .MuiInputAdornment-root': {
              color: theme.palette.text.secondary,
              transition: 'color 0.2s ease',
            },

            '&.Mui-focused .MuiInputAdornment-root': {
              color: theme.palette.primary.main,
            },
          }}
          disabled={!!props.disabled}
          value={props.value || ''}
          onChange={onChange}
          inputRef={props.inputRef}
          onFocus={handleFocus}
          onBlur={handleBlur}
          required={props.required}
          onPaste={props.onPaste}
          inputProps={inputProps}
          onClick={props.onClick}
          onKeyDown={props.onKeyDown}
          onWheel={handleWheel}
        />

        {/* Frontend Validation Error Message */}
        {hasFrontendError && (
          <FormHelperText
            error
            sx={{
              margin: '6px 4px 0 4px',
              fontSize: '0.75rem',
              fontWeight: 500,
              color: theme.palette.error.main,
              letterSpacing: '0.2px',
              animation: 'slideInDown 0.2s ease',

              '@keyframes slideInDown': {
                from: {
                  opacity: 0,
                  transform: 'translateY(-4px)',
                },
                to: {
                  opacity: 1,
                  transform: 'translateY(0)',
                },
              },
            }}
          >
            {typeof props.error === 'string' ? props.error : 'Invalid Data'}
          </FormHelperText>
        )}

        {/* Character Count */}
        {showCharCount && charCount !== undefined && (
          <div
            style={{
              textAlign: 'right',
              marginTop: '6px',
              fontSize: '0.7rem',
              fontWeight: 500,
              color: hasError ? theme.palette.error.main : theme.palette.text.secondary,
              letterSpacing: '0.2px',
              transition: 'color 0.2s ease',
            }}
          >
            {charCount}
            {maxLength ? `/${maxLength}` : ''}
          </div>
        )}
      </FormControl>

      {/* Floating Premium AI Tooltip/Popover */}
      <Popper
        open={isPopoverOpen}
        anchorEl={containerRef.current}
        placement="bottom-start"
        transition
        disablePortal={false}
        modifiers={[
          {
            name: 'preventOverflow',
            enabled: true,
            options: {
              altAxis: true,
              padding: 8,
            },
          },
        ]}
        style={{ zIndex: 1300, width: containerRef.current ? containerRef.current.clientWidth : 320, maxWidth: 360 }}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={250}>
            <Paper
              onMouseEnter={() => setIsPopoverHovered(true)}
              onMouseLeave={() => setIsPopoverHovered(false)}
              sx={{
                mt: 1,
                p: 2,
                borderRadius: '16px',
                position: 'relative',
                overflow: 'hidden',
                ...paperStyle,
              }}
            >
              {tooltipInnerContent}
            </Paper>
          </Fade>
        )}
      </Popper>
    </div>
  );
}

export default SbTextField;
